import React from 'react';
import { Link } from 'react-router-dom';
import PortfolioShell from '../Portfolio/PortfolioShell';
import NoteViews from '../Portfolio/NoteViews';
import { projects, notes, skillGroups, getProjectNotes } from '../../content/portfolioContent';
import loomImage from '../../images/loom-project-2-optimized.webp';
import worldAuthoringImage from '../../images/world-authoring-terrain-landscape.webp';
import './Home.css';

const projectArtwork = {
  loom: { src: loomImage, alt: 'A luminous mechanical loom weaving threads of cyan, violet, and gold.', height: 801 },
  'world-authoring-lab': { src: worldAuthoringImage, alt: 'Teal valleys and purple mountain ridges in the Terrain Lab.', width: 1613, height: 808 }
};

export function Home() {
  const selectedProjects = projects
    .filter((project) => project.showOnHome !== false)
    .slice(0, 4)
    .map((project) => ({
      ...project,
      artwork: project.homeCompact ? null : projectArtwork[project.slug],
      noteCount: getProjectNotes(project.slug).length
    }));
  const selectedNotes = notes.filter((note) => note.featured).slice(0, 3);
  const skillLinks = skillGroups.map((group) => ({
    title: group.title,
    href: '/background#' + group.id
  }));

  return (
    <PortfolioShell className="portfolio-home">
      <section className="connect-hero pf-home-hero" id="home" aria-labelledby="home-heading">
          <p className="connect-eyebrow">Software architecture · Applied AI</p>
          <h1 id="home-heading">Robert Miller</h1>
          <p className="connect-intro pf-home-hero-intro">
            Software engineer. Painting with code to make complex systems easier to understand.
          </p>
          <p className="pf-home-hero-subintro">
            Systems architecture, automation, and AI-assisted development.
          </p>
          <div className="connect-actions">
            <Link className="connect-action connect-action-primary" to="/#showcase">Explore the work <span aria-hidden="true">↓</span></Link>
            <Link className="connect-action connect-action-secondary" to="/notes">Read the notes <span aria-hidden="true">→</span></Link>
          </div>
      </section>

      <section className="pf-home-work" id="showcase" aria-labelledby="work-heading">
        <div className="portfolio-container">
          <div className="connect-section-heading pf-home-work-heading">
            <div>
              <h2 id="work-heading">Selected work</h2>
              <p>I use these systems to speed up my own work and, paradoxically, to rely less on LLMs and agents when the lights go off.</p>
            </div>
          </div>
          <div className="pf-home-projects">
            {selectedProjects.map((project, index) => (
              <Link className={'connect-project-card pf-home-project' + (project.artwork ? ' pf-home-project-illustrated' : ' pf-home-project-text')} to={'/projects/' + project.slug} aria-labelledby={'home-project-' + project.slug} key={project.slug}>
                {project.artwork && (
                  <div className="pf-home-project-media">
                    <img className="pf-home-project-image" src={project.artwork.src} alt={project.artwork.alt} width={project.artwork.width || 1200} height={project.artwork.height} loading="lazy" decoding="async" />
                    <span className="pf-home-project-number" aria-hidden="true">{String(index + 1).padStart(2, '0')}</span>
                  </div>
                )}
                <div className="pf-home-project-content">
                <h3 id={'home-project-' + project.slug}>{project.homeTitle || project.title}</h3>
                <p className="pf-home-project-summary">{project.homeSummary || project.summary}</p>
                {project.artwork && (
                  <>
                    <div className="pf-home-project-details">
                      <div>
                        <h4>Why?</h4>
                        <p>{project.problem}</p>
                      </div>
                      <div>
                        <h4>How it works</h4>
                        <p>{project.approach.slice(0, 2).join(' ')}</p>
                      </div>
                    </div>
                    <div className="pf-home-project-practice">
                      <h4>In practice</h4>
                      <p>{project.demonstrates[0]}</p>
                    </div>
                  </>
                )}
                {!project.artwork && (
                  <div className="pf-home-project-practice">
                    <h4>In practice</h4>
                    <p>{project.demonstrates[0]}</p>
                  </div>
                )}
                <div className="pf-home-project-bottom">
                  <span className="pf-home-project-note-count">{project.noteCount} working {project.noteCount === 1 ? 'note' : 'notes'}</span>
                  <span className="pf-home-project-action">Read the project overview <span aria-hidden="true">→</span></span>
                </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="pf-home-notes" aria-labelledby="notes-heading">
        <div className="portfolio-container pf-home-notes-layout">
          <div className="pf-home-notes-intro">
            <h2 id="notes-heading">Working notes</h2>
          </div>
          <NoteViews notes={selectedNotes} label="Featured working notes" />
        </div>
      </section>

      <section className="pf-home-background" id="about" aria-labelledby="background-heading">
        <div className="portfolio-container pf-home-background-layout">
          <div>
            <p className="pf-eyebrow">Background</p>
            <h2 id="background-heading">Conventional software work.</h2>
            <p>
              Before and alongside these projects, I’ve worked on secure web applications, migration tools,
              internal systems, performance improvements, and software used by real people. The common thread is
              making useful work take fewer steps, fewer clicks, and less effort.
            </p>
            <a className="pf-text-link" href="https://www.linkedin.com/in/rpmiller4" target="_blank" rel="noreferrer">Full work history on LinkedIn <span aria-hidden="true">↗</span></a>
          </div>
          <div className="pf-home-capabilities" id="skillset">
            {skillLinks.map((item, index) => (
              <Link to={item.href} key={item.title}>
                <span className={'pf-home-capability-dot pf-home-capability-dot-' + index} aria-hidden="true" />
                <span>{item.title}</span>
                <span aria-hidden="true">↗</span>
              </Link>
            ))}
            <p>Practical skills and tools, without the full resume ceremony.</p>
          </div>
        </div>
      </section>

      <section className="pf-home-contact" id="contact" aria-labelledby="contact-heading">
        <div className="portfolio-container">
          <div className="pf-home-contact-card">
            <div>
              <p className="pf-eyebrow">Asynchronous Communication</p>
              <h2 id="contact-heading">Let’s compare notes.</h2>
              <p>Architecture, applied AI, or a problem that needs a clearer shape.</p>
            </div>
            <Link className="connect-action connect-action-primary" to="/connect">Connect with me <span aria-hidden="true">↗</span></Link>
          </div>
        </div>
      </section>
    </PortfolioShell>
  );
}
